import { useEffect, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import React from "react";

import { Divider } from "../Divider";

// The viz runs on its own subdomain and rebuilds the CKAN top bar, so it can't see the
// portal's server-side login state. It asks CKAN via GET /api/pdp/whoami (credentialed,
// same-site cookie) and renders either the anonymous Log in / Register links or the real
// account bar (avatar / dashboard / settings / log out) to match data.pattaya.go.th.
// See opend-ckan controllers/whoami.py.

const baseUrl = window.__env__.BASE_CKAN_URL;

type WhoAmI = {
  logged_in: boolean;
  user_name?: string;
  display_name?: string;
  is_sysadmin?: boolean;
  image_url?: string | null;
  new_activities?: number;
};

// Absolute avatar URL (CKAN may return an uploaded /uploads/... path or a gravatar URL).
const avatarSrc = (image_url?: string | null) => {
  if (!image_url) return null;
  return image_url.startsWith("http") ? image_url : `${baseUrl}${image_url}`;
};

// --- Inline SVG icons (the viz has no icon font). 24x24 stroke, sized by the wrapper. ---
type IconProps = { className?: string };
const svgProps = {
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
};

const UsersIcon = ({ className }: IconProps) => (
  <svg className={className} {...svgProps}>
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);
const BarChartIcon = ({ className }: IconProps) => (
  <svg className={className} {...svgProps}>
    <line x1="12" y1="20" x2="12" y2="10" />
    <line x1="18" y1="20" x2="18" y2="4" />
    <line x1="6" y1="20" x2="6" y2="16" />
  </svg>
);
const ShieldIcon = ({ className }: IconProps) => (
  <svg className={className} {...svgProps}>
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    <path d="M9 12l2 2 4-4" />
  </svg>
);
const GaugeIcon = ({ className }: IconProps) => (
  <svg className={className} {...svgProps}>
    <path d="M3.5 19a9 9 0 1 1 17 0" />
    <line x1="12" y1="15" x2="15.5" y2="9.5" />
    <circle cx="12" cy="15" r="1.4" fill="currentColor" stroke="none" />
  </svg>
);
const GearIcon = ({ className }: IconProps) => (
  <svg className={className} {...svgProps}>
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);
const LogoutIcon = ({ className }: IconProps) => (
  <svg className={className} {...svgProps}>
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <polyline points="16 17 21 12 16 7" />
    <line x1="21" y1="12" x2="9" y2="12" />
  </svg>
);
const UserIcon = ({ className }: IconProps) => (
  <svg className={className} {...svgProps}>
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const iconLinkClass =
  "flex items-center justify-center h-[40px] px-2 text-white hover:bg-[var(--sky-primary-hover)]";
const iconClass = "w-[18px] h-[18px]";

export const UserMenu = () => {
  const { t } = useTranslation();
  // Start anonymous: it's the safe fallback if /api/pdp/whoami never resolves (network
  // error, allow_all=* blocking the credentialed read, or a signed-out browser).
  const [user, setUser] = useState<WhoAmI | null>(null);

  useEffect(() => {
    let alive = true;
    axios
      .get<WhoAmI>(`${baseUrl}/api/pdp/whoami`, {
        withCredentials: true,
        timeout: 8000,
      })
      .then((res) => {
        if (alive && res.data && res.data.logged_in) setUser(res.data);
      })
      .catch(() => {
        /* stay anonymous */
      });
    return () => {
      alive = false;
    };
  }, []);

  // --- Anonymous: original Log in / Register links ---
  if (!user || !user.logged_in) {
    const items = [
      { label: t("header.login"), url: `${baseUrl}/user/login` },
      { label: t("header.register"), url: `${baseUrl}/user/register` },
    ];
    return (
      <div className="h-[40px] flex">
        {items.map((item, idx) => (
          <React.Fragment key={`${idx}-${item.label}`}>
            <Divider vertical />
            <a
              className="flex items-center text-white hover:underline hover:bg-[var(--sky-primary-hover)] md:px-2 text-nowrap"
              href={item.url}
            >
              {item.label}
            </a>
          </React.Fragment>
        ))}
      </div>
    );
  }

  // --- Logged in: mirror CKAN's nav_logged.html account bar ---
  const img = avatarSrc(user.image_url);
  const newActivities = user.new_activities || 0;

  return (
    <div className="h-[40px] flex items-center">
      {user.is_sysadmin && (
        <a
          className={iconLinkClass}
          href={`${baseUrl}/user`}
          title={t("header.users")}
        >
          <UsersIcon className={iconClass} />
        </a>
      )}

      <a
        className={iconLinkClass}
        href={`${baseUrl}/site_stats`}
        title={t("header.site_stats")}
      >
        <BarChartIcon className={iconClass} />
      </a>

      {user.is_sysadmin && (
        <a
          className={iconLinkClass}
          href={`${baseUrl}/ckan-admin`}
          title={t("header.admin")}
        >
          <ShieldIcon className={iconClass} />
        </a>
      )}

      {/* Profile: avatar + display name */}
      <a
        className="flex items-center gap-[6px] h-[40px] px-2 text-white hover:bg-[var(--sky-primary-hover)] text-nowrap"
        href={`${baseUrl}/user/${user.user_name}`}
        title={t("header.profile")}
      >
        {img ? (
          <img
            src={img}
            alt=""
            className="w-[22px] h-[22px] rounded-full object-cover bg-white"
          />
        ) : (
          <UserIcon className="w-[20px] h-[20px]" />
        )}
        <span>{user.display_name}</span>
      </a>

      {/* Dashboard + new-activity badge */}
      <a
        className="flex items-center gap-[4px] h-[40px] px-2 text-white hover:bg-[var(--sky-primary-hover)]"
        href={`${baseUrl}/dashboard`}
        title={t("header.dashboard")}
      >
        <GaugeIcon className={iconClass} />
        <span
          className={`min-w-[18px] text-center text-[11px] leading-[18px] rounded-full px-[5px] ${
            newActivities > 0
              ? "bg-red-500 text-white"
              : "bg-white/80 text-[var(--sky-primary-active)]"
          }`}
        >
          {newActivities}
        </span>
      </a>

      <a
        className={iconLinkClass}
        href={`${baseUrl}/user/edit/${user.user_name}`}
        title={t("header.settings")}
      >
        <GearIcon className={iconClass} />
      </a>

      <a
        className={iconLinkClass}
        href={`${baseUrl}/user/_logout`}
        title={t("header.logout")}
      >
        <LogoutIcon className={iconClass} />
      </a>
    </div>
  );
};
